import React, {useEffect, useRef} from 'react';
import {loadScriptAndWaitForGlobal} from "@wix/devzai-utils-dom";
import {LocationCoordinates, Nullable} from "@wix/devzai-utils-common";
import {getGoogleMapStyles} from './google-map-styles';

export const GoogleMapElement = React.memo(function GoogleMapElement(props: GoogleMapElement.Props) {
    const {
        locationCoordinates: {
            latitude,
            longitude
        },
        zoom,
        languageCode,
        googleMapsApiKey,
        disableMapInteractions,
        onMapReady,
        contentWindow,
        className,
        isDarkMode,
        ...htmlAttributes
    } = props;

    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<Nullable<google.maps.Map>>(null);
    const markerInstanceRef = useRef<Nullable<google.maps.Marker>>(null);

    useEffect(() => {
        const loadMapSDK = () => {
            return new Promise<void>(resolve => {
                if (contentWindow.google?.maps?.Map) {
                    resolve();
                    return;
                }

                void loadScriptAndWaitForGlobal(
                    `https://maps.googleapis.com/maps/api/js?loading=async&libraries=marker&key=${googleMapsApiKey}&language=${languageCode}`,
                    'google.maps.Map',
                    {skipIfAlreadyLoaded: true, rootWindow: contentWindow}
                ).then(() => resolve());
            });
        };

        const initializeMap = () => {
            if (!mapContainerRef.current || !contentWindow.google?.maps?.marker || !googleMapsApiKey) return;

            const mapProps = {
                zoom: zoom,
                center: { lat: latitude, lng: longitude },
                disableDefaultUI: true,
                ...disableMapInteractions ? {
                    gestureHandling: 'none',
                    zoomControl: false,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                    scrollwheel: false,
                    draggable: false,
                    keyboardShortcuts: false,
                } : {
                    zoomControl: true
                },
            };

            mapInstanceRef.current = new contentWindow.google.maps.Map(
                mapContainerRef.current,
                mapProps
            );

            markerInstanceRef.current = new contentWindow.google.maps.Marker({
                position: { lat: latitude, lng: longitude },
                map: mapInstanceRef.current,
            });

            const mapStyles = getGoogleMapStyles(isDarkMode);
            mapInstanceRef.current.setOptions({ styles: mapStyles });

            onMapReady?.();
        };

        const initialLoad = async () => {
            await loadMapSDK();
            return initializeMap();
        }

        void initialLoad();
    }, []);

    useEffect(() => {
        if (mapInstanceRef.current) {
            const newCenter = { lat: latitude, lng: longitude };
            mapInstanceRef.current.setCenter(newCenter);
            mapInstanceRef.current.setZoom(zoom);

            if (markerInstanceRef.current) {
                markerInstanceRef.current.setPosition(newCenter);
            }
        }
    }, [latitude, longitude, zoom]);

    useEffect(() => {
        if (mapInstanceRef.current) {
            const mapStyles = getGoogleMapStyles(isDarkMode);
            mapInstanceRef.current.setOptions({ styles: mapStyles });
        }
    }, [isDarkMode]);

    return (
        <div
            {...htmlAttributes}
            style={{ position: 'absolute', inset: '0px', ...htmlAttributes.style }}
            ref={mapContainerRef}
            title={`Map view at ${latitude},${longitude}`}
        />
    );
});

export namespace GoogleMapElement {

    export interface Props extends React.HTMLAttributes<any> {
        locationCoordinates: LocationCoordinates;
        zoom: number;
        languageCode: string;
        googleMapsApiKey: string;
        disableMapInteractions: boolean;
        onMapReady?: () => void;
        contentWindow: Window & { google?: typeof google };
        isDarkMode?: boolean;
    }
}

